import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Route as ToolsWorkflowRoute } from 'src/routes/tools/workflows/$workflowId.index';
import { Route as WorkflowCreateRoute } from 'src/routes/tools/workflows/create';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Tabs } from 'src/modules/shared/components/atoms/tabs/tabs';
import { Tab } from 'src/modules/shared/components/atoms/tabs/tab';
import { useActions } from 'src/modules/shared/hooks/api/automations/actions';
import { useAutomationsWorkflows } from 'src/modules/shared/hooks/api/automations/workflows/workflows';
import type { WorkflowMetadata } from 'src/api/generated';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { CardHeader } from 'src/modules/shared/components/atoms/cards/card-header';
import { CardBody } from 'src/modules/shared/components/atoms/cards/card-body';
import { CardFooter } from 'src/modules/shared/components/atoms/cards/card-footer';
import { CardSkeleton } from 'src/modules/shared/components/atoms/cards/card-skeleton';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';

export const ToolsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'basic' | 'custom'>('custom');
  const {
    actions,
    loading: loadingActions,
    error: actionsError,
  } = useActions(50, activeTab === 'basic');
  const {
    workflows,
    loading: loadingWorkflows,
    error: workflowsError,
  } = useAutomationsWorkflows(50, activeTab === 'custom');

  return (
    <div className="flex flex-col h-full">
      <Panel className="h-full flex flex-col">
        <PanelHeader
          fixed
          className="px-4 py-3 border-b"
          actions={
            activeTab === 'custom' ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  void navigate({
                    to: WorkflowCreateRoute.to,
                    search: { phoneVersion: 'v1' },
                  })
                }
              >
                Crear flujo
              </Button>
            ) : null
          }
        >
          <Text as="h2" size="h2" weight="medium">
            Herramientas
          </Text>
        </PanelHeader>
        <div className="flex-1 overflow-auto p-4">
          <div className="mb-4">
            <Tabs
              value={activeTab}
              onChange={(v) => setActiveTab(v as 'basic' | 'custom')}
              appearance="soft"
              allowWrap
            >
              <Tab value="custom" label="Personalizadas" />
              <Tab value="basic" label="Básicas" />
            </Tabs>
          </div>

          {activeTab === 'basic' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loadingActions && (
                <>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <CardSkeleton
                      key={i}
                      header={{ title: true, subtitle: true }}
                      content={{ lines: 2, widths: ['w-4/5', 'w-3/5'] }}
                    />
                  ))}
                </>
              )}
              {actionsError && (
                <ErrorState title="No se pudieron cargar las acciones" />
              )}
              {!loadingActions && !actionsError && actions.length === 0 && (
                <div className="col-span-full flex items-center justify-center py-8">
                  <EmptyState
                    title="No hay acciones"
                    description="Aún no hay acciones disponibles."
                    appearance="soft"
                    variant="neutral"
                  />
                </div>
              )}
              {!loadingActions &&
                !actionsError &&
                actions.length > 0 &&
                actions.map((action, idx) => (
                  <Card
                    key={action.name || idx}
                    appearance="outline"
                    variant="neutral"
                    shadow="sm"
                  >
                    <CardHeader>
                      <Text weight="semibold" className="text-black">
                        {action.name}
                      </Text>
                    </CardHeader>
                    <CardBody>
                      <Text className="text-sm text-black">
                        {nonEmptyString(action.description) ?? '—'}
                      </Text>
                    </CardBody>
                  </Card>
                ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loadingWorkflows && (
                <>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <CardSkeleton
                      key={i}
                      header={{ title: true, subtitle: true }}
                      content={{ lines: 2, widths: ['w-4/5', 'w-3/5'] }}
                    />
                  ))}
                </>
              )}
              {workflowsError && (
                <ErrorState title="No se pudieron cargar los flujos" />
              )}
              {!loadingWorkflows &&
                !workflowsError &&
                workflows.length === 0 && (
                  <div className="col-span-full flex items-center justify-center py-8">
                    <EmptyState
                      title="No hay flujos"
                      description="Aún no hay flujos personalizados disponibles."
                      appearance="soft"
                      variant="neutral"
                    />
                  </div>
                )}
              {!loadingWorkflows &&
                !workflowsError &&
                workflows.length > 0 &&
                workflows.map((wf: WorkflowMetadata) => (
                  <Card
                    key={wf.workflowId}
                    appearance="outline"
                    variant="neutral"
                    shadow="sm"
                    onClick={() =>
                      void navigate({
                        to: ToolsWorkflowRoute.to,
                        params: { workflowId: wf.workflowId },
                        search: { phoneVersion: 'v1' },
                      })
                    }
                  >
                    <CardHeader>
                      <Text weight="semibold" className="text-black">
                        {nonEmptyString(wf.name) ?? wf.workflowId}
                      </Text>
                    </CardHeader>
                    <CardBody>
                      <Text className="text-sm text-black">
                        {nonEmptyString(wf.description) ?? '—'}
                      </Text>
                    </CardBody>
                    <CardFooter className="flex justify-end">
                      <Button
                        variant="neutral"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          void navigate({
                            to: WorkflowCreateRoute.to,
                            search: {
                              phoneVersion: 'v1',
                              fromWorkflowId: wf.workflowId,
                            },
                          });
                        }}
                      >
                        Crear copia
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
};

export default ToolsPage;
